import { MessageType, TwoFactorAuthMethod } from "@obg/ngx.core";

import { TwoFactorAuthIconDetails } from "./two-factor-auth.model";

export enum OperationType {
	SuspiciousLogin = "SuspiciousLogin",
	TwoFactorVerification = "TwoFactorVerification",
	PinwallAuthentication = "PinwallAuthentication",
	RegistrationVerification = "RegistrationVerification",
}

export enum CommunicationChannel {
	Unset = "Unset",
	Email = "Email",
	SMS = "SMS",
	EmailAndSMS = "EmailAndSMS",
}

export enum TwoFactorAuthDialogStep {
	SelectMethod = "SelectMethod",
	ReSelectMethod = "ReSelectMethod",
	LoginPassword = "LoginPassword",
	ForgotPassword = "ForgotPassword",
	SMSVerificationCode = "SMSVerificationCode",
	EmailVerificationCode = "EmailVerificationCode",
	RemoveMethodConfirmation = "RemoveMethodConfirmation",
}

type MessageFunction = (
	communicationChannel: CommunicationChannel.Email | CommunicationChannel.SMS
) => TwoFactorAuthMessageType | undefined;

export const twoFactorSuccessMessageTypeTree: Record<TwoFactorAuthMethodType, MessageFunction> = {
	Setup: (communicationChannel: CommunicationChannel.Email | CommunicationChannel.SMS) =>
		TwoFactorAuthMessageType[`${communicationChannel}EnableSuccess`],
	MarkAsPrimary: (communicationChannel: CommunicationChannel.Email | CommunicationChannel.SMS) =>
		TwoFactorAuthMessageType[`${communicationChannel}MarkAsPrimaryMethodSuccess`],
	Remove: (communicationChannel: CommunicationChannel.Email | CommunicationChannel.SMS) =>
		TwoFactorAuthMessageType[`${communicationChannel}RemoveMethodSuccess`],
	RemoveAll: () => TwoFactorAuthMessageType[`RemoveMethodsSuccess`],
	GetAll: () => undefined,
};

export enum TwoFactorAuthMessageType {
	SMSSendSuccess = "SMSSendSuccess",
	EmailSendSuccess = "EmailSendSuccess",
	SMSEnableSuccess = "SMSEnableSuccess",
	EmailEnableSuccess = "EmailEnableSuccess",
	EmailMarkAsPrimaryMethodSuccess = "EmailMarkAsPrimaryMethodSuccess",
	SMSMarkAsPrimaryMethodSuccess = "SMSMarkAsPrimaryMethodSuccess",
	EmailRemoveMethodSuccess = "EmailRemoveMethodSuccess",
	SMSRemoveMethodSuccess = "SMSRemoveMethodSuccess",
	CommonSendError = "CommonSendError",
	RemoveMethodsSuccess = "RemoveMethodsSuccess",
	RemoveRememberDeviceSuccess = "RemoveRememberDeviceSuccess",
}

export const twoFactorAuthMessageDictionary: Record<TwoFactorAuthMessageType, { message: string; type: MessageType }> =
	{
		SMSSendSuccess: { message: "two-factor-auth.message.sms-send-success", type: MessageType.Success },
		EmailSendSuccess: { message: "two-factor-auth.message.email-send-success", type: MessageType.Success },
		SMSEnableSuccess: { message: "two-factor-auth.message.sms-enable-success", type: MessageType.Success },
		EmailEnableSuccess: { message: "two-factor-auth.message.email-enable-success", type: MessageType.Success },
		EmailMarkAsPrimaryMethodSuccess: {
			message: "two-factor-auth.message.email-mark-as-primary-method-success",
			type: MessageType.Success,
		},
		SMSMarkAsPrimaryMethodSuccess: {
			message: "two-factor-auth.message.sms-mark-as-primary-method-success",
			type: MessageType.Success,
		},
		EmailRemoveMethodSuccess: {
			message: "two-factor-auth.message.email-remove-method-success",
			type: MessageType.Success,
		},
		SMSRemoveMethodSuccess: {
			message: "two-factor-auth.message.sms-remove-method-success",
			type: MessageType.Success,
		},
		CommonSendError: { message: "two-factor-auth.message.common-send-error", type: MessageType.Error },
		RemoveMethodsSuccess: { message: "two-factor-auth.message.remove-method-success", type: MessageType.Success },
		RemoveRememberDeviceSuccess: {
			message: "two-factor-auth.message.remove-remember-device-success",
			type: MessageType.Success,
		},
	};

export enum TwoFactorAuthMethodType {
	GetAll = "GetAll",
	Setup = "Setup",
	MarkAsPrimary = "MarkAsPrimary",
	Remove = "Remove",
	RemoveAll = "RemoveAll",
}

export const enum TwoFactorAuthCodeBEVerificationErrors {
	E_AUTHENTICATIONCHALLENGES_MAXATTEMPTSREACHED = "E_AUTHENTICATIONCHALLENGES_MAXATTEMPTSREACHED",
	E_SESSIONS_LOGIN_INVALIDCREDENTIALS = "E_SESSIONS_LOGIN_INVALIDCREDENTIALS",
	E_SECURITYOPTIONS_INVALID2FATOKEN = "E_SECURITYOPTIONS_INVALID2FATOKEN",
	E_AUTHENTICATIONCHALLENGES_INVALIDTOKEN = "E_AUTHENTICATIONCHALLENGES_INVALIDTOKEN",
	E_SESSIONS_LOGIN_MAXATTEMPTSREACHED = "E_SESSIONS_LOGIN_MAXATTEMPTSREACHED",
	E_SESSIONS_LOGIN_2FA_MAXIMUM_ATTEMPTS_REACHED = "E_SESSIONS_LOGIN_2FA_MAXIMUM_ATTEMPTS_REACHED",
	E_CREDENTIALS_PASSWORD_CHANGE_VERIFICATION_CODE_INVALID = "E_CREDENTIALS_PASSWORD_CHANGE_VERIFICATION_CODE_INVALID",
	E_UNHANDLED = "E_UNHANDLED",
	E_PHONENUMBERVERIFICATIONCODE_NOTFOUND = "E_PHONENUMBERVERIFICATIONCODE_NOTFOUND",
}

export const enum TwoFactorAuthCodeCommonVerificationErrors {
	ExpiredCode = "ExpiredCode",
	MaxLoginAttemptReached = "MaxLoginAttemptReached",
	InvalidServerResponse = "InvalidServerResponse",
	MaxLoginAttemptReachedSetupFlow = "MaxLoginAttemptReachedSetupFlow",
}

export type TwoFactorAuthCodeVerificationErrors =
	| TwoFactorAuthCodeBEVerificationErrors
	| TwoFactorAuthCodeCommonVerificationErrors;

export const twoFactorAuthCodeVerificationErrorDictionary: Record<TwoFactorAuthCodeVerificationErrors, string> = {
	E_SESSIONS_LOGIN_INVALIDCREDENTIALS: "two-factor-auth.message.invalid-code",
	E_SECURITYOPTIONS_INVALID2FATOKEN: "two-factor-auth.message.invalid-code",
	E_AUTHENTICATIONCHALLENGES_INVALIDTOKEN: "two-factor-auth.message.invalid-code",
	E_AUTHENTICATIONCHALLENGES_MAXATTEMPTSREACHED: "two-factor-auth.dialog.user-locked-text",
	E_SESSIONS_LOGIN_2FA_MAXIMUM_ATTEMPTS_REACHED: "two-factor-auth.dialog.user-locked-text",
	E_CREDENTIALS_PASSWORD_CHANGE_VERIFICATION_CODE_INVALID: "two-factor-auth.message.invalid-code",
	E_UNHANDLED: "two-factor-auth.message.contact-customer-support",
	ExpiredCode: "two-factor-auth.message.expired-code",
	InvalidServerResponse: "two-factor-auth.message.invalid-server-response",
	MaxLoginAttemptReached: "two-factor-auth.message.max-attempt-reached",
	E_SESSIONS_LOGIN_MAXATTEMPTSREACHED: "two-factor-auth.dialog.user-locked-text",
	MaxLoginAttemptReachedSetupFlow: "two-factor-auth.message.max-attempt-reached-setup-flow-custom-message",
	E_PHONENUMBERVERIFICATIONCODE_NOTFOUND: "two-factor-auth.message.invalid-code",
};

export enum TwoFactorAuthFlow {
	SetupMethodFlow = "setupMethodFlow",
	RemoveMethodFlow = "removeMethodFlow",
	LoginFlow = "loginMethodFlow",
	ChangePhoneFlow = "changeNumberFlow",
}

export enum TwoFactorChallengeType {
	RegularAuthentication = "regularAuthentication",
	SuspiciousLogin = "suspiciousLogin",
}

export const authMethodIconDetailsDictionary: Record<TwoFactorAuthMethod, TwoFactorAuthIconDetails> = {
	EmailTwoFactorAuthenticationMethod: {
		className: "ico-mail",
		testId: "verification-code-icon-mail",
	},
	SmsTwoFactorAuthenticationMethod: {
		className: "ico-two-factor-sms",
		testId: "verification-code-icon-sms",
	},
};

export enum TwoFactorAuthRemoveType {
	RemoveEmailMethod = "RemoveEmailMethod",
	RemoveSmsMethod = "RemoveSmsMethod",
	RemoveBothMethod = "RemoveBothMethod",
}

export const BROWSER_LIST = ["FIREFOX", "BLINK", "SAFARI", "TRIDENT", "EDGE", "WEBKIT"];
